import React, { useState, useMemo, useRef, useEffect } from 'react';
import { AnalysisResult } from '../services/projectService';
import {
  reanalyzeResult,
  getResultHistory,
  validateApplicability,
  assessRisk,
  bulkAssessRiskAI
} from '../services/resultService';
import './AnalysisResultsList.css';
import './Tooltip.css';
import { FaFileExcel, FaCalculator, FaCheckDouble, FaFilter, FaArrowUp, FaArrowDown, FaQuestionCircle, FaSearch, FaSortAmountDown } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import HistoryModal from './HistoryModal';
import ContextModal from './ContextModal';
import AnalysisResultCard from './AnalysisResultCard';
import Tooltip from './Tooltip';

/** Wraps a button so the tooltip still shows when the button is disabled */
const ButtonWithTooltip: React.FC<{
  tooltip: string;
  children: React.ReactNode;
}> = ({ tooltip, children }) => (
  <span className="button-tooltip-wrapper">
    {children}
    <span className="button-tooltip-trigger">
      <FaQuestionCircle className="tooltip-icon" />
      <span className="tooltip-text">{tooltip}</span>
    </span>
  </span>
);

interface AnalysisResultsListProps {
  results: AnalysisResult[];
  projectName: string;
  projectId: string;
}

const AnalysisResultsList: React.FC<AnalysisResultsListProps> = ({ results, projectName, projectId }) => {
  const queryClient = useQueryClient();

  // --- All Mutations ---
  const reanalyzeMutation = useMutation({
    mutationFn: ({ resultId, context }: { resultId: number; context?: string }) => reanalyzeResult(resultId, context),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (error: any) => {
      alert(`Failed to re-analyze: ${error.message}`);
    }
  });

  const validateApplicabilityMutation = useMutation({
    mutationFn: ({ resultId, applies }: { resultId: number; applies: boolean }) => validateApplicability(resultId, applies),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (error: any) => {
      alert(`Failed to validate: ${error.message}`);
    }
  });

  const assessRiskMutation = useMutation({
    mutationFn: ({ resultId, probability, impact }: { resultId: number; probability: number; impact: number }) => assessRisk(resultId, probability, impact),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (error: any) => {
      alert(`Failed to assess risk: ${error.message}`);
    }
  });

  const bulkConfirmMutation = useMutation({
    mutationFn: (items: AnalysisResult[]) => {
      const promises = items.map(result => validateApplicability(result.id, result.result!.applies));
      return Promise.all(promises);
    },
    onSuccess: (data) => {
      alert(`${data.length} items have been confirmed successfully.`);
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (error: any) => {
      alert(`An error occurred during bulk confirmation: ${error.message}`);
    }
  });

  const bulkAssessMutation = useMutation({
    mutationFn: () => bulkAssessRiskAI(projectId),
    onSuccess: (data) => {
      alert(data.message);
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (error: any) => {
      alert(`An error occurred during bulk assessment: ${error.message}`);
    }
  });

  const [showAppliedOnly, setShowAppliedOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyData, setHistoryData] = useState<AnalysisResult[]>([]);
  const [isContextModalOpen, setIsContextModalOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState<AnalysisResult | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set());
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const statusFilterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isStatusFilterOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(e.target as Node)) {
        setIsStatusFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isStatusFilterOpen]);

  const [sortConfig, setSortConfig] = useState({ key: 'inherent_risk_score', direction: 'desc' });

  const itemsPendingReview = useMemo(() => 
    results.filter(r => r.workflow_stage === 'pending_applicability_review' && r.result),
    [results]
  );

  const allStatuses = useMemo(() => 
    Array.from(new Set(results.map(r => r.workflow_stage))),
    [results]
  );

  const itemsPendingRiskAssessment = useMemo(() =>
    results.filter(r => r.workflow_stage === 'pending_risk_assessment'),
    [results]
  );

  const handleBulkConfirm = async () => {
    if (itemsPendingReview.length === 0) return;
    const confirmed = window.confirm(`Are you sure you want to accept the AI suggestion for all ${itemsPendingReview.length} pending items? This action cannot be undone.`);
    if (confirmed) {
      bulkConfirmMutation.mutate(itemsPendingReview);
    }
  };

  const handleBulkAssess = async () => {
    if (itemsPendingRiskAssessment.length === 0) return;
    const confirmed = window.confirm(`Are you sure? This will run ${itemsPendingRiskAssessment.length} AI tasks.`);
    if (confirmed) {
      bulkAssessMutation.mutate();
    }
  };

  const getDefaultDirection = (key: string): 'asc' | 'desc' => {
    if (key === 'workflow_stage') return 'asc';
    return 'desc';
  };

  const toggleSortDirection = () => {
    setSortConfig(prev => ({ ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const handleStatusFilterChange = (status: string) => {
    setSelectedStatuses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  const handleOpenContextModal = (result: AnalysisResult) => {
    setSelectedResult(result);
    setIsContextModalOpen(true);
  };

  const handleSaveContext = async (newContext: string) => {
    if (!selectedResult) return;
    reanalyzeMutation.mutate({ resultId: selectedResult.id, context: newContext });
    setIsContextModalOpen(false);
  };

  const handleHistoryClick = async (resultId: number) => {
    try {
      const history = await getResultHistory(resultId);
      setHistoryData(history);
      setIsHistoryModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch result history:', error);
      alert('Failed to fetch result history.');
    }
  };

  const handleReanalyzeClick = async (resultId: number) => {
    if (!window.confirm('Are you sure you want to re-analyze this item? This will create a new version.')) {
      return;
    }
    reanalyzeMutation.mutate({ resultId });
  };

  const handleValidateApplicability = async (resultId: number, applies: boolean) => {
    validateApplicabilityMutation.mutate({ resultId, applies });
  };
  
  const handleAssessRisk = async (resultId: number) => {
    // This part remains the same as it uses prompts
    const probabilityString = prompt("Enter Probability (1-5):", "5");
    if (probabilityString === null) return;
    const impactString = prompt("Enter Impact (1--5):", "5");
    if (impactString === null) return;
    
    const probability = parseInt(probabilityString, 10);
    const impact = parseInt(impactString, 10);

    if (isNaN(probability) || isNaN(impact) || probability < 1 || probability > 5 || impact < 1 || impact > 5) {
      alert("Invalid input. Please enter numbers between 1 and 5.");
      return;
    }
    
    assessRiskMutation.mutate({ resultId, probability, impact });
  };

  const handleExport = () => {
    // 1. Flatten the data
    const flattenedData = results.map(row => ({
      'Ley': row.matrix_item.ley,
      'Artículo': row.matrix_item.articulo,
      'Categoría': row.matrix_item.categoria,
      'Grupo de Delitos': row.matrix_item.grupo_delitos,
      'Workflow Stage': row.workflow_stage,
      // Clear distinction between AI suggestion and human validation
      'AI Suggestion Applies': row.result?.applies,
      'Final Decision Applies': row.validated_applies,
      'Probability': row.probability_score,
      'Impact': row.impact_score,
      'Inherent Risk': row.inherent_risk_score,
      'Risk Level': row.inherent_risk_level,
      'AI Justification': row.result?.justification,
    }));

    // 2. Create worksheet and workbook
    const worksheet = XLSX.utils.json_to_sheet(flattenedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Analysis Results');

    // 3. Trigger the download with a dynamic filename
    const fileName = `AnalysisResults-${projectName.replace(/ /g, '_')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const filteredResults = useMemo(() => {
    let filtered = results;

    if (showAppliedOnly) {
      filtered = filtered.filter(r => r.validated_applies === true || (r.validated_applies === null && r.result?.applies === true));
    }

    if (selectedStatuses.size > 0) {
      filtered = filtered.filter(r => selectedStatuses.has(r.workflow_stage));
    }

    if (searchQuery) {
      const lowercasedQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(result => {
        const { matrix_item, result: llmResult } = result;
        return (
          matrix_item.ley.toLowerCase().includes(lowercasedQuery) ||
          matrix_item.articulo.toLowerCase().includes(lowercasedQuery) ||
          matrix_item.categoria.toLowerCase().includes(lowercasedQuery) ||
          matrix_item.grupo_delitos.toLowerCase().includes(lowercasedQuery) ||
          (llmResult?.justification && llmResult.justification.toLowerCase().includes(lowercasedQuery))
        );
      });
    }

    return filtered;
  }, [results, showAppliedOnly, searchQuery, selectedStatuses]);

  const sortedResults = useMemo(() => {
    const sortable = [...filteredResults];
    if (!sortConfig) return sortable;

    sortable.sort((a, b) => {
        const { key, direction } = sortConfig;
        
        let valA: string | number | null | undefined;
        let valB: string | number | null | undefined;

        if (key === 'inherent_risk_score') {
            valA = a.inherent_risk_score;
            valB = b.inherent_risk_score;
        } else if (key === 'workflow_stage') {
            valA = a.workflow_stage;
            valB = b.workflow_stage;
        } else if (key === 'confidence_score') {
            valA = a.result?.confidence_score;
            valB = b.result?.confidence_score;
        }

        if (valA == null) return 1;
        if (valB == null) return -1;

        const dir = direction === 'asc' ? 1 : -1;

        if (typeof valA === 'string' && typeof valB === 'string') {
            return valA.localeCompare(valB) * dir;
        }
        
        if (typeof valA === 'number' && typeof valB === 'number') {
            return (valA - valB) * dir;
        }
        
        return 0;
    });

    return sortable;
  }, [filteredResults, sortConfig]);

  const isUpdating = reanalyzeMutation.isPending || validateApplicabilityMutation.isPending || assessRiskMutation.isPending;
  const currentUpdatingId = isUpdating ? (reanalyzeMutation.variables?.resultId ?? validateApplicabilityMutation.variables?.resultId ?? assessRiskMutation.variables?.resultId ?? null) : null;

  if (!results || results.length === 0) {
    return (
      <div className="results-list-placeholder">
        <h2>No Analysis Results</h2>
        <p>Trigger an analysis to see the results here.</p>
      </div>
    );
  }

  return (
    <div className="analysis-results-container">
      <div className="results-toolbar">
        {/* ---- Filters Box ---- */}
        <div className="toolbar-box filters-box">
          <div className="toolbar-box-header">
            <FaFilter className="toolbar-box-icon" />
            <span className="toolbar-box-title">Filters</span>
            <Tooltip text="Narrow down results by keyword, status, or applicability." />
          </div>
          <div className="toolbar-box-body">
            <div className="search-bar-container">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by law, article, category..."
                className="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="toolbar-controls-row">
              <div className="filter-container" ref={statusFilterRef}>
                <button className="toolbar-control-btn" onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)}>
                  Status ({selectedStatuses.size > 0 ? selectedStatuses.size : 'All'})
                  <span className={`accordion-icon ${isStatusFilterOpen ? 'open' : ''}`}>▼</span>
                </button>
                {isStatusFilterOpen && (
                  <div className="filter-dropdown">
                    {allStatuses.map(status => (
                      <label key={status} className="filter-dropdown-item">
                        <input
                          type="checkbox"
                          checked={selectedStatuses.has(status)}
                          onChange={() => handleStatusFilterChange(status)}
                        />
                        {status.replace(/_/g, ' ')}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="sort-container">
                <FaSortAmountDown className="sort-icon" />
                <div className="sort-control-wrapper">
                  <select
                    className="sort-select"
                    value={sortConfig.key}
                    onChange={(e) => setSortConfig({ key: e.target.value, direction: getDefaultDirection(e.target.value) })}
                  >
                    <option value="inherent_risk_score">Risk Score</option>
                    <option value="workflow_stage">Status</option>
                    <option value="confidence_score">Confidence</option>
                  </select>
                  <button className="sort-direction-button" onClick={toggleSortDirection} title={`Sort ${sortConfig.direction === 'asc' ? 'Descending' : 'Ascending'}`}>
                    {sortConfig.direction === 'asc' ? <FaArrowUp /> : <FaArrowDown />}
                  </button>
                </div>
              </div>
              <div className="filter-group">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={showAppliedOnly}
                    onChange={() => setShowAppliedOnly(!showAppliedOnly)}
                  />
                  <span className="slider"></span>
                </label>
                <span className="toggle-label">Applied Only</span>
              </div>
            </div>
          </div>
        </div>

        {/* ---- Actions Box ---- */}
        <div className="toolbar-box actions-box">
          <div className="toolbar-box-header">
            <FaCheckDouble className="toolbar-box-icon" />
            <span className="toolbar-box-title">Actions</span>
            <Tooltip text="Bulk operations and data export for the current results." />
          </div>
          <div className="toolbar-box-body actions-body">
            {itemsPendingReview.length > 0 && (
              <ButtonWithTooltip tooltip={`Confirm AI applicability suggestions for all ${itemsPendingReview.length} pending items at once.`}>
                <button
                  className="toolbar-action-btn btn-success"
                  onClick={handleBulkConfirm}
                  disabled={bulkConfirmMutation.isPending}
                >
                  <FaCheckDouble />
                  {bulkConfirmMutation.isPending ? 'Confirming...' : `Accept All (${itemsPendingReview.length})`}
                </button>
              </ButtonWithTooltip>
            )}
            {itemsPendingRiskAssessment.length > 0 && (
              <ButtonWithTooltip tooltip={`Run AI risk assessment for ${itemsPendingRiskAssessment.length} items that have been validated but not yet scored.`}>
                <button
                  className="toolbar-action-btn btn-primary"
                  onClick={handleBulkAssess}
                  disabled={bulkAssessMutation.isPending}
                >
                  <FaCalculator />
                  {bulkAssessMutation.isPending ? 'Assessing...' : `AI Risk Assessment (${itemsPendingRiskAssessment.length})`}
                </button>
              </ButtonWithTooltip>
            )}
            <ButtonWithTooltip tooltip="Download all analysis results as an Excel spreadsheet.">
              <button className="toolbar-action-btn btn-export" onClick={handleExport}>
                <FaFileExcel />
                Export to Excel
              </button>
            </ButtonWithTooltip>
            {itemsPendingReview.length === 0 && itemsPendingRiskAssessment.length === 0 && (
              <span className="actions-empty-hint">No bulk actions available</span>
            )}
          </div>
        </div>
      </div>
      <div className="results-grid">
        {sortedResults.map((result) => (
          <AnalysisResultCard
            key={result.id}
            result={result}
            isUpdating={isUpdating}
            currentUpdatingId={currentUpdatingId}
            onReanalyze={handleReanalyzeClick}
            onAssessRisk={handleAssessRisk}
            onHistoryClick={handleHistoryClick}
            onOpenContext={handleOpenContextModal}
            onValidateApplicability={handleValidateApplicability}
          />
        ))}
      </div>
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        history={historyData}
      />
      {selectedResult && (
        <ContextModal
          isOpen={isContextModalOpen}
          onClose={() => setIsContextModalOpen(false)}
          onSave={handleSaveContext}
          initialContext={selectedResult.additional_context}
          isSaving={reanalyzeMutation.isPending && reanalyzeMutation.variables?.resultId === selectedResult.id}
        />
      )}
    </div>
  );
};

export default AnalysisResultsList; 